/**
 * session.ts — mirrors the Firebase port's auth/session.ts, but with
 * Supabase Auth's signInWithPassword instead of signInWithEmailAndPassword.
 * Preserves the exact same Session/useSession/currentSession/signOut
 * interface so the ~80 other files that read useSession() don't need to
 * change. `role`/`employeeId`/`name` come from app_metadata (see
 * DATA_MODEL.md "Auth") — set only by adminSaveOperator via the Admin API,
 * never by the user, so it can't be spoofed client-side.
 */
import { createStore } from '../lib/store';
import { clearCache } from '../lib/swr';
import { supabase } from '../lib/supabaseClient';

export interface Session {
  token: string;
  operatorId: string;
  operatorName: string;
  role: 'operator' | 'admin';
}

const store = createStore<Session | null>(null, 'logbook.session');

export const useSession = () => store.use();

/** The stored session, if any. */
export function currentSession(): Session | null {
  return store.get();
}

export function signOut(): void {
  store.set(null);
  clearCache();
  void supabase.auth.signOut();
}

// Strips ALL whitespace, not just leading/trailing — some live Employee IDs have an internal
// space (e.g. "GN 2300032"), which would otherwise produce an invalid email address. Must stay
// in sync with the same function in supabase/functions/api/_shared/constants.ts.
function syntheticEmailFor(employeeId: string): string {
  return `${employeeId.trim().toLowerCase().replace(/\s+/g, '')}@unm-electrical.local`;
}

export async function signIn(operatorId: string, password: string): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: syntheticEmailFor(operatorId),
      password,
    });
    if (error || !data.session || !data.user) {
      const message = error?.message === 'Invalid login credentials'
        ? 'Incorrect Employee ID or password.'
        : (error?.message || 'Could not sign in.');
      return { ok: false, message };
    }
    const meta = data.user.app_metadata as { role?: 'admin' | 'operator'; employeeId?: string; name?: string };
    const role: 'admin' | 'operator' = meta.role === 'admin' ? 'admin' : 'operator';
    store.set({
      token: data.session.access_token,
      operatorId: (meta.employeeId ?? operatorId).trim(),
      operatorName: meta.name ?? operatorId.trim(),
      role,
    });
    // warm the read caches in the background so the first screen paints instantly
    void import('../lib/prefetch').then((m) => m.warmCaches(role)).catch(() => {});
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Could not sign in.' };
  }
}

/** Keeps store.token fresh as Supabase silently refreshes the session in the background —
 * call once at app startup, before anything reads currentSession(). */
export function startSessionTokenRefresh(): void {
  supabase.auth.onAuthStateChange((event, session) => {
    const current = store.get();
    if (event === 'SIGNED_OUT' || !session) {
      if (current) store.set(null);
      return;
    }
    if (current) store.set({ ...current, token: session.access_token });
  });
}
